import { faCog } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FacilityNormalized } from "EntityTypes";
import { useAllFacilities } from "modules/facilities/facilityHooks";
import { useMemo, useCallback } from "react";
import { Button, Form } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { useHistory, useLocation } from "react-router";

const DEFAULT_ROWS_PER_PAGE = 10;

const FacilitiesTable: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [facilities] = useAllFacilities();
  
  // Derive state from URL - URL is the source of truth
  const urlParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get("search") || "",
      page: parseInt(params.get("page") || "1", 10),
      rowsPerPage: parseInt(params.get("rowsPerPage") || String(DEFAULT_ROWS_PER_PAGE), 10)
    };
  }, [location.search]);

  const filterText = urlParams.search;
  const currentPage = urlParams.page;
  const rowsPerPage = urlParams.rowsPerPage;

  // Update URL when search changes
  const handleSearchChange = (value: string) => {
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }
    // Reset to page 1 when searching
    params.delete("page");
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  };

  // Update URL when page changes
  const handlePageChange = useCallback((page: number) => {
    const params = new URLSearchParams(location.search);
    if (page > 1) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  }, [location.pathname, location.search, history]);

  // Update URL when rows per page changes
  const handleRowsPerPageChange = useCallback((newRowsPerPage: number, page: number) => {
    const params = new URLSearchParams(location.search);
    if (newRowsPerPage !== DEFAULT_ROWS_PER_PAGE) {
      params.set("rowsPerPage", newRowsPerPage.toString());
    } else {
      params.delete("rowsPerPage");
    }
    // Update page if needed
    if (page > 1) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }
    const newUrl = params.toString() 
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newUrl);
  }, [location.pathname, location.search, history]);

  const filteredFacilities = useMemo(() => {
    if (!filterText) return facilities;
    return facilities?.filter((facility) =>
      facility.name.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [facilities, filterText]);

  const columns: TableColumn<FacilityNormalized>[] = useMemo(
    () => [
      {
        selector: (row) => row.name,
        name: "Facility Name",
        sortable: true,
      },
      {
        name: "Number of Sims",
        selector: (row) => row.simulators.length,
        sortable: true,
      },
      {
        name: "Number of Users",
        selector: (row) => row?.controlUserIds.length,
        sortable: true,
      },
      {
        cell: (row) => (
          <Button 
            onClick={() => {
              // Preserve search and page in URL when navigating to facility
              history.push(`facilities/${row.id}${location.search}`);
            }}
          >
            <FontAwesomeIcon icon={faCog} />
          </Button>
        ),
        ignoreRowClick: true,
        button: true,
        allowOverflow: true,
      },
    ],
    [history, location.search]
  );

  return (
    <div className='m-4'>
      <DataTable
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Facilities</span>
            <Form.Control
              type='text'
              placeholder='Search facilities...'
              value={filterText}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ maxWidth: "300px" }}
            />
          </div>
        }
        columns={columns}
        data={filteredFacilities}
        dense
        highlightOnHover
        pagination
        paginationDefaultPage={currentPage}
        paginationPerPage={rowsPerPage}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        responsive
      />
    </div>
  );
};

export default FacilitiesTable;
